import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Typography,
  Box,
} from '@mui/material';
import slugify from 'slugify';
import { useListProductsQuery } from '../services/products.endpoints';

interface StableSlugSelectorProps {
  value: string;
  category: string;
  onChange: (value: string) => void;
}

const StableSlugSelector: React.FC<StableSlugSelectorProps> = ({
  value,
  category,
  onChange,
}) => {
  // Fetch all products (including hidden) so we can list all existing families
  const { data: products = [], isLoading } = useListProductsQuery({
    includeHidden: true,
  });
  const [options, setOptions] = useState<string[]>([]);
  const [typedSlug, setTypedSlug] = useState(value || '');

  useEffect(() => {
    const allSlugs = Array.from(
      new Set(
        products
          .map((p: any) => p.stableSlug)
          .filter((s: string | undefined): s is string => !!s),
      ),
    ).sort();
    setOptions(allSlugs);
  }, [products]);

  useEffect(() => {
    setTypedSlug(value);
  }, [value]);

  const handleInputChange = (_: any, newValue: string) => {
    const slugified = slugify(newValue, { lower: true, strict: true });
    setTypedSlug(slugified);
    onChange(slugified);
  };

  const isNewFamily = typedSlug && !options.includes(typedSlug) && !isLoading;

  return (
    <Box>
      <Autocomplete
        freeSolo
        disableClearable
        value={typedSlug || ''}
        onInputChange={handleInputChange}
        options={options}
        loading={isLoading}
        renderInput={(params) => (
          <TextField
            {...params}
            required
            error={!typedSlug}
            label="Product Family (required)"
            placeholder="Enter family name"
            // helperText={
            //   !typedSlug
            //     ? 'Family name is required. Use lowercase, no spaces, no version numbers.'
            //     : 'Enter product name with no spaces, caps or version numbers.'
            // }
            helperText={
              category === 'Upgrade'
                ? 'Use product name, version numbers, lowercase, no spaces.'
                : 'Use product name, lowercase, no spaces, no version numbers.'
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {isNewFamily && typedSlug && (
        <Typography
          variant="caption"
          color="primary"
          sx={{ mt: 0.5, display: 'block' }}
        >
          Creating new family: <strong>{typedSlug}</strong>
        </Typography>
      )}
    </Box>
  );
};

export default StableSlugSelector;
